import { Col } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { noop } from 'lodash-es';

import { useReduxDialogControls } from '../../../common/utils/dialogUtils';
import { formatCodeValue } from '../../../common/utils/formatUtils';
import IconButton from '../../../components/buttons/IconButton';
import {
  SimpleCard,
  SimpleCardTheme,
} from '../../../components/layout/cardElements';
import { LabelRow, LabelWithValue } from '../../../components/typography';
import {
  DateTimeFormatNumericNoYear,
  DateTimeFormatShortWithDashes,
} from '../../../utils/dateFormatting';
import {
  ShipmentSensorWidgetsDrawer,
  computeSensorMode,
  normalizeAddonData,
} from '../ShipmentSensorWidgets';
import { FAIcon } from '../../../components/adapters/fontAwesomeAdapters';
import Collapsible from '../../../components/layout/Collapsible';
import { useShipmentSummariesCommonStyles } from './shipmentSummariesCommon.styles';

export const ShipmentSummaryCard = props => (
  <SimpleCard {...props} theme={SimpleCardTheme.FLAT} />
);
export function ShipmentSummaryCardSection({
  className,
  header,
  headerId,
  children,
  collapsible,
}) {
  const [expanded, setExpanded] = useState(true);
  const { styles } = useShipmentSummariesCommonStyles();

  return (
    <div className={classNames(styles.section, { collapsible }, className)}>
      {(header || headerId) && (
        <div
          className={styles.sectionHeader}
          onClick={collapsible ? () => setExpanded(!expanded) : noop}
        >
          {collapsible && (
            <FAIcon
              icon={expanded ? 'caret-up' : 'caret-down'}
              className="icon-16"
            />
          )}
          {headerId ? <FormattedMessage id={headerId} /> : header}
        </div>
      )}
      {header || headerId ? (
        <div className={styles.sectionBody}>
          {collapsible ? (
            <Collapsible expanded={expanded} dynamicContent>
              {children}
            </Collapsible>
          ) : (
            children
          )}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export const ShipmentSummaryLabelWithValue = props => (
  <LabelWithValue {...props} nowrap />
);

export const TwoLineLabelWithValue = props => (
  <ShipmentSummaryLabelWithValue {...props} labelSize="full" />
);

export const ShipmentSummaryDateTimeFormatFlight = props => (
  <DateTimeFormatNumericNoYear {...props} separator=" | " />
);
export const ShipmentSummaryDateTimeFormatWithPipe = props => (
  <DateTimeFormatShortWithDashes {...props} separator=" | " />
);

export function ShipmentDeviceAddon({
  data,
  jobNumber,
  packageIndex,
  index,
  isLast,
}) {
  const { index: addonIndex = 0, type } = data || {};
  const { isOpen, open, close } = useReduxDialogControls({
    id: 'shipmentDetail.sensorWidgets',
    spec: { packageIndex, addonIndex, type },
  });
  const addonData = normalizeAddonData(data);
  const { styles } = useShipmentSummariesCommonStyles();

  if (!data) {
    return null;
  }
  const { brandName, modelName, source, sensorData, sensorUrl, serialNumber } =
    data;

  const sensorMode = computeSensorMode({ sensorData, sensorUrl });

  return (
    <div className={styles.deviceAddonRow}>
      <div
        className={classNames(styles.deviceAddonRowHeader, {
          active: isOpen,
        })}
      >
        <div className={styles.deviceAddonRowIndex}>{index + 1}</div>
        <div className={styles.deviceAddonRowHeaderLabel}>
          <FormattedMessage id="shipmentDetail.deviceAddon.serialNumber" />
        </div>
        <div className={styles.deviceAddonRowHeaderValue}>
          {serialNumber || '-'}
        </div>
        <IconButton
          icon="external-link-square-alt icon-16"
          onClick={open}
          disabled={!sensorMode}
        />
      </div>
      <div>
        <LabelRow
          className={classNames(styles.deviceAddonLabelRow, {
            [styles.deviceAddonLabelRowLast]: isLast,
          })}
        >
          <Col span={12}>
            <ShipmentSummaryLabelWithValue
              labelId="shipmentDetail.deviceAddon.brandName"
              text={brandName}
              labelSize="full"
            />
          </Col>
          <Col span={8}>
            <ShipmentSummaryLabelWithValue
              labelId="shipmentDetail.deviceAddon.modelName"
              text={modelName}
              labelSize="full"
            />
          </Col>
          <Col span={4}>
            <ShipmentSummaryLabelWithValue
              labelId="shipmentDetail.deviceAddon.source"
              text={formatCodeValue(source)}
              labelSize="full"
            />
          </Col>
        </LabelRow>
      </div>
      <ShipmentSensorWidgetsDrawer
        jobNumber={jobNumber}
        addonData={addonData}
        isOpen={isOpen}
        onClose={close}
      />
    </div>
  );
}
